import { useState, useCallback, useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"
import { downloadExport } from "../../../helpers/downloadExport"
import { useParams, useSearchParams } from "react-router-dom"
import {
  Badge,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Select,
  Rating,
  Button,
  Tooltip,
  Card,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { modals } from "@mantine/modals"
import { DataTable, useDataTable } from "../../../components/DataTable"
import type { Column } from "../../../components/DataTable"
import { useDeleteAgencyVisit, useVisitAgencies, useVisitUsers } from "../hooks/useAgencyVisits"
import { AgencyVisitSchema } from "../models/agencyVisit"
import type { AgencyVisit } from "../models/agencyVisit"
import { useUser } from "../../user/hooks/useUser"
import { PageLayout, PageHeader } from "../../../components/crud"
import AgencyVisitDrawer from "./AgencyVisitDrawer"
import type { DrawerMode } from "./AgencyVisitDrawer"

const STATUS_MAP: Record<number, { label: string; color: string }> = {
  5: { label: "Agendada", color: "indigo" },
  10: { label: "Visitada", color: "teal" },
}

export default function AgencyVisitList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyVisits ?? false
  const canViewAll = userData?.can?.viewMenuAgencyAllVisits ?? false
  const canViewAny = canManage || canViewAll
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, filters, updateFilters } =
    useDataTable<AgencyVisit>({
      queryKey: "agency-visits",
      url: "agency-visit",
      schema: AgencyVisitSchema,
      defaultSort: { field: "date", dir: "desc" },
      defaultPageSize: 50,
    })

  const { data: agencies } = useVisitAgencies()
  const { data: users } = useVisitUsers()
  const deleteMutation = useDeleteAgencyVisit()

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null)

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedVisitId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedVisitId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: t("agencyVisits.confirmDelete"),
      children: <Text size="sm">{t("agencyVisits.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(id),
    })
  }

  const columns: Column<AgencyVisit>[] = [
    ...(canViewAny
      ? [{ key: "agency_name" as const, label: t("agencyVisits.agency"), sortable: true, width: 200 }]
      : []),
    { key: "reason", label: t("agencyVisits.reason"), sortable: true },
    {
      key: "date",
      label: t("agencyVisits.date"),
      sortable: true,
      width: 130,
      render: (v) => new Date(v.date).toLocaleDateString("pt-PT"),
    },
    {
      key: "users",
      label: t("agencyVisits.visitors"),
      width: 200,
      render: (v) => v.users.map((u) => u.fullName).join(", "),
    },
    {
      key: "rating",
      label: t("agencyVisits.rating"),
      sortable: true,
      width: 140,
      render: (v) => (v.rating != null ? <Rating value={v.rating} readOnly size="sm" /> : null),
    },
    {
      key: "status",
      label: t("agencyVisits.status"),
      sortable: true,
      width: 110,
      render: (v) => (
        <Badge color={STATUS_MAP[v.status]?.color ?? "gray"} variant="light" size="sm">
          {STATUS_MAP[v.status]?.label ?? v.status}
        </Badge>
      ),
    },
  ]

  const agencyOptions = (agencies ?? []).map((a: { id: number; name: string }) => ({
    value: String(a.id),
    label: a.name,
  }))

  const userOptions = (users ?? []).map((u: { id: number; fullName: string }) => ({
    value: String(u.id),
    label: u.fullName,
  }))

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("agencyVisits.title")}
          actions={
            <Group>
              <Button
                variant="default"
                onClick={() => downloadExport("agency-visit/export", "visitas_agencias.xlsx", filters as Record<string, string | undefined>)}
                leftSection={<i className="fa fa-file-excel-o" />}
              >
                <Trans i18nKey="common.export" />
              </Button>
              {canManage && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="agencyVisits.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md">
        <Group grow align="flex-end">
          {canViewAny && (
            <Select
              label={t("agencyVisits.filterAgency")}
              placeholder={t("agencyVisits.selectAgency")}
              data={agencyOptions}
              searchable
              clearable
              onChange={(val) => updateFilters({ agency_id: val })}
            />
          )}
          <TextInput
            label={t("agencyVisits.reason")}
            placeholder={t("agencyVisits.searchReason")}
            onChange={(e) => updateFilters({ reason: e.currentTarget.value })}
          />
          <DateInput
            label={t("agencyVisits.filterStartDate")}
            placeholder=""
            valueFormat="YYYY-MM-DD"
            clearable
            onChange={(val) => updateFilters({ start_date: val ? val.toISOString().split("T")[0] : undefined })}
          />
          <DateInput
            label={t("agencyVisits.filterEndDate")}
            placeholder=""
            valueFormat="YYYY-MM-DD"
            clearable
            onChange={(val) => updateFilters({ end_date: val ? val.toISOString().split("T")[0] : undefined })}
          />
          <Select
            label={t("agencyVisits.filterVisitor")}
            placeholder={t("agencyVisits.selectVisitors")}
            data={userOptions}
            searchable
            clearable
            onChange={(val) => updateFilters({ user: val })}
          />
          <Select
            label={t("agencyVisits.filterStatus")}
            placeholder={t("agencyVisits.filterStatus")}
            data={[
              { value: "5", label: t("agencyVisits.scheduled") },
              { value: "10", label: t("agencyVisits.visited") },
            ]}
            clearable
            onChange={(val) => updateFilters({ status: val })}
          />
        </Group>
      </Card>

      <DataTable<AgencyVisit>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("agencyVisits.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={(v) => openDrawer(v.id, "view")}
        actions={(v) => (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            {canManage && (
              <>
                <Tooltip label={t("common.edit")}>
                  <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.id, "edit")}>
                    <i className="fa fa-pencil" />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t("common.delete")}>
                  <ActionIcon variant="subtle" size="md" color="red" onClick={() => handleDelete(v.id)}>
                    <i className="fa fa-trash" />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        )}
      />

      <AgencyVisitDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedVisitId={selectedVisitId}
        onDeleted={handleDeleted}
        onSwitchToEdit={() => setDrawerMode("edit")}
      />
    </PageLayout>
  )
}
